// fleet-generated: do not edit by hand
//
// Copied verbatim  : infra/fleet/artifacts/events/event-bus.ts
// Parameters       : none — below this header, identical across the fleet
// Destination      : packages/fleet/events/src/event-bus.ts
//
// TO CHANGE THIS FILE, edit the canonical copy in infra and re-render. It
// changes for the whole fleet at once. Editing it here is reverted by the
// next render, and the guard in this repo goes red in the meantime:
//
//     cd ../infra && node fleet/fleet.ts render --repo gym-log-app
//     node fleet/fleet.ts check --repo gym-log-app
//
// The generator runs LOCALLY and is never invoked by CI. CI only verifies
// that what is committed matches the hash in .github/fleet-lock.json, which
// also records WHICH infra commit produced it. The commit is deliberately
// not repeated here: one value, one place — and stamping it into every file
// meant a one-line template edit rewrote four files in 28 repos.
import { type AnyActorRef, assign, setup } from "xstate";
import type { AppEvent, EventPayload, EventType } from "./event-types";

/**
 * Event Bus - Centralized pub/sub for decoupled feature communication
 *
 * Implemented as an XState actor that:
 * - Maintains a registry of subscribers by event type
 * - Buffers events during periods when no subscribers are listening
 * - Provides selective subscription (subscribe to specific event types)
 * - Maintains event history for debugging
 */

// ============================================
// Types
// ============================================
export interface Subscriber {
  id: string;
  eventTypes: EventType[] | "*"; // "*" means all events
  callback: (event: AppEvent) => void;
}

export interface EventBusContext {
  subscribers: Map<string, Subscriber>;
  eventHistory: { event: AppEvent; timestamp: number; subscriberCount: number }[];
  buffer: AppEvent[]; // Events buffered when no matching subscribers
  maxHistorySize: number;
  maxBufferSize: number;
}

export type EventBusEvent =
  | { type: "SUBSCRIBE"; subscriber: Subscriber }
  | { type: "UNSUBSCRIBE"; subscriberId: string }
  | { type: "EMIT"; event: AppEvent }
  | { type: "CLEAR_HISTORY" }
  | { type: "CLEAR_BUFFER" };

const MAX_HISTORY_SIZE = 100;
const MAX_BUFFER_SIZE = 50;

// ============================================
// Event Bus Machine
// ============================================
export const eventBusMachine = setup({
  types: {
    // oxlint-disable-next-line typescript/no-unsafe-type-assertion
    context: {} as EventBusContext,
    // oxlint-disable-next-line typescript/no-unsafe-type-assertion
    events: {} as EventBusEvent,
  },
  actions: {
    addSubscriber: assign({
      subscribers: ({ context, event }) => {
        if (event.type !== "SUBSCRIBE") {
          return context.subscribers;
        }
        return new Map([...context.subscribers, [event.subscriber.id, event.subscriber]]);
      },
    }),

    removeSubscriber: assign({
      subscribers: ({ context, event }) => {
        if (event.type !== "UNSUBSCRIBE") {
          return context.subscribers;
        }
        const newSubscribers = new Map(context.subscribers);
        newSubscribers.delete(event.subscriberId);
        return newSubscribers;
      },
    }),

    bufferEvent: assign({
      buffer: ({ context, event }) => {
        if (event.type !== "EMIT") {
          return context.buffer;
        }
        // Check if any subscriber matches
        const hasMatchingSubscriber = [...context.subscribers.values()].some(
          (sub) => sub.eventTypes === "*" || sub.eventTypes.includes(event.event.type),
        );
        // Only buffer if no subscribers matched and buffer not full
        if (!hasMatchingSubscriber && context.buffer.length < context.maxBufferSize) {
          return [...context.buffer, event.event];
        }
        return context.buffer;
      },
    }),

    emitEvent: assign({
      eventHistory: ({ context, event }) => {
        if (event.type !== "EMIT") {
          return context.eventHistory;
        }

        // Notify all matching subscribers
        let subscriberCount = 0;
        for (const subscriber of context.subscribers.values()) {
          const matchesEvent =
            subscriber.eventTypes === "*" || subscriber.eventTypes.includes(event.event.type);

          if (matchesEvent) {
            subscriber.callback(event.event);
            subscriberCount += 1;
          }
        }

        // Add to history
        const newHistory = [
          ...context.eventHistory,
          { event: event.event, timestamp: Date.now(), subscriberCount },
        ];

        // Trim history if needed
        if (newHistory.length > context.maxHistorySize) {
          return newHistory.slice(newHistory.length - context.maxHistorySize);
        }

        return newHistory;
      },
    }),

    clearHistory: assign({
      eventHistory: [],
    }),

    clearBuffer: assign({
      buffer: [],
    }),
  },
}).createMachine({
  id: "eventBus",
  context: {
    subscribers: new Map(),
    eventHistory: [],
    buffer: [],
    maxHistorySize: MAX_HISTORY_SIZE,
    maxBufferSize: MAX_BUFFER_SIZE,
  },
  on: {
    SUBSCRIBE: {
      actions: "addSubscriber",
    },
    UNSUBSCRIBE: {
      actions: "removeSubscriber",
    },
    EMIT: {
      actions: ["emitEvent", "bufferEvent"],
    },
    CLEAR_HISTORY: {
      actions: "clearHistory",
    },
    CLEAR_BUFFER: {
      actions: "clearBuffer",
    },
  },
});

// ============================================
// Helper Functions for Subscription Management
// ============================================
let subscriberIdCounter = 0;

/**
 * Subscribe to specific event types
 */
export function subscribeToEvents(
  actorRef: AnyActorRef,
  eventTypes: EventType[] | "*",
  callback: (event: AppEvent) => void,
): () => void {
  subscriberIdCounter += 1;
  const id = `sub_${subscriberIdCounter}_${Date.now()}`;

  actorRef.send({
    type: "SUBSCRIBE",
    subscriber: { id, eventTypes, callback },
  });

  // Return unsubscribe function
  return () => {
    actorRef.send({ type: "UNSUBSCRIBE", subscriberId: id });
  };
}

/**
 * Type guard to safely narrow AppEvent to a specific event payload
 */
function isEventOfType<T extends EventType>(
  event: AppEvent,
  eventType: T,
): event is EventPayload<T> {
  return (event.type as string) === eventType;
}

/**
 * Subscribe to a single event type
 */
export function subscribeToEvent<T extends EventType>(
  actorRef: AnyActorRef,
  eventType: T,
  callback: (payload: EventPayload<T>) => void,
): () => void {
  return subscribeToEvents(actorRef, [eventType], (event) => {
    if (isEventOfType(event, eventType)) {
      callback(event);
    }
  });
}

/**
 * Emit an event to the bus
 */
export function emitEvent(actorRef: AnyActorRef, event: AppEvent): void {
  actorRef.send({ type: "EMIT", event });
}

/**
 * Create typed event emitters for a feature
 *
 * Currently active: emitAuth (for SESSION_EXPIRED)
 *
 * NOTE: Only coordination events (Auth, System, Theme).
 * Domain data mutations are handled automatically by PowerSync.
 */
export function createFeatureEmitters(actorRef: AnyActorRef) {
  return {
    emitAuth: (event: Extract<AppEvent, { type: `AUTH.${string}` }>) => {
      emitEvent(actorRef, event);
    },
  };
}
